//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import FlagIcon from '@material-ui/icons/Flag';
import { VerifiedUserRounded } from '@material-ui/icons';
// import Button from '@material-ui/core/Button';
import TextTruncate from 'react-text-truncate';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getUserDateFormat } from '../../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdBadgeCount from '../../common/elemental/AfitdBadgeCount';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import LastMessageAgeCounter from './LastMessageAgeCounter';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
// import { format } from 'date-fns';
// import _range from 'lodash/range';
import _get from 'lodash/get';
// import _set from 'lodash/set';
// import _forEach from 'lodash/forEach';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        //chatLeftBlock2In
        root: {
            display: 'flex',
            padding: '14px 24px 14px 18px',
            borderLeft: '4px solid transparent',
            '&:hover': {
                borderLeft: '4px solid #1D8B8C',
                background: '#FCFCFC',
                cursor: 'pointer',
            },
        },
        currentThread: {
            borderLeft: '4px solid #1D8B8C',
            background: '#FCFCFC',
            '&:hover': {
                cursor: 'auto',
            },
        },
        // .chatLeftBlock2In.active{border-left: 4px solid #1d8b8c;background: #FCFCFC;}

        profileImage: {
            // clipPath: 'circle(50% at center)',
            // '-webkit-clip-path': 'circle(50% at center)',
            width: 40,
            height: 40,
        },
        messageLeft: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginRight: '16px',
            width: '42px',
        },
        messageLeftLink: {
            display: 'block',
            clipPath: 'circle(60px at center)',
        },
        flagIconContainer: {
            marginTop: '5px',
        },
        messageRight: {
            flexGrow: 1,
        },

        messageRightLine1: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        // .messageRight h6
        companyNameContainer: {
            position: 'relative',
            fontSize: '14px',
            color: '#9c9e9d',
            padding: '0 0 6px 0',
            fontWeight: 'normal',
            lineHeight: '16px',
            display: 'flex',
        },
        verifyIcon: {
            fontSize: '18px',
            color: '#1D8A8B',
            margin: '2px 5px',
        },
        // .messageRight h6 span
        lastMessageDt: {
            fontSize: '14px',
            color: '#9c9e9d',
        },

        messageRightLine2: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        // .messageRight h4
        subject: {
            position: 'relative',
            fontSize: '16px',
            color: '#131f15',
            padding: '0 50px 6px 0',
            fontWeight: '500',
            lineHeight: '22px',
        },
        // .messageRight h4 span
        unreadMessageCount: {},

        messageRightLine3: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
        },
        // .messageRight span
        lastMessage: {
            color: '#111E14',
            fontSize: '14px',
            fontWeight: 'normal',
            lineHeight: '18px',
            display: 'block',
            position: 'relative',
            width: '100%',
        },
        draftReply: {
            color: 'red',
            fontStyle: 'italic',
        },
        // .messageRight span .ToolTip{position: absolute;right: -6px;top: -17px;cursor: pointer;z-index: 999;}

        hidden: {
            display: 'none',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, userDateFormat, userId, currentThreadId, draftReplies } = params;
    const { data, onClick } = props;
    const {
        root: rootCls,
        currentThread: currentThreadCls,
        flagIconContainer: flagIconContainerCls,
        lastMessage: lastMessageCls,
        draftReply: draftReplyCls,
        hidden: hiddenCls,
    } = classes;

    const threadId = _get(data, 'threadId');
    const companyName = _get(data, 'companyName');
    const profileImageUrl = _get(data, 'companyProfilePicUrl');
    const subject = _get(data, 'jobTitle');
    const jobSeekerNewMessagesCount = _get(data, 'unreadCount', 0);

    const lastMessageDt = _get(data, 'lastMessageDt');
    const lastMessage = _get(data, 'lastMessage');
    const markedForFollowUp = _get(data, `followUpDt.${userId}`);

    const isCurrentThread = currentThreadId === threadId;

    let draftReply = null;
    if (!isCurrentThread) {
        draftReply = _get(draftReplies, `current.${threadId}`, '');
    }

    const defns = {
        root: {
            className: cx(rootCls, { [currentThreadCls]: isCurrentThread }),
            onClick: onClick.bind(null, data, currentThreadId),
        },
        profileImage: {
            src: profileImageUrl,
            alt: `${companyName} logo`,
        },
        companyNameText: {
            variant: 'body2',
            clsMidGrayColor: true,
            value: companyName,
        },
        lastMessageDtText: {
            typographyProps: {
                variant: 'body2',
                clsMidGrayColor: true,
            },
            dt: lastMessageDt,
            dateFormat: userDateFormat,
        },
        subjectText: {
            variant: 'body1',
            clsLightBold: true,
            value: subject,
        },
        unreadMessageCount: {
            className: cx({ [hiddenCls]: isCurrentThread }),
        },
        unreadMessageCountText: {
            count: jobSeekerNewMessagesCount,
        },
        lastMessage: {
            className: cx(lastMessageCls, { [draftReplyCls]: !isCurrentThread && draftReply }),
        },
        lastMessageText: {
            variant: 'body2',
            value: isCurrentThread || !draftReply ? lastMessage : `Draft: ${draftReply}`,
        },
        flagIconContainer: {
            className: cx(flagIconContainerCls, { [hiddenCls]: !markedForFollowUp }),
        },
        flagIcon: {
            color: 'primary',
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ThreadSummaryBlock = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const userId = _get(appContext, 'userId');
    const draftReplies = _get(appContext, 'draftReplies');
    const currentThreadId = _get(context, 'currentThreadId');
    const userDateFormat = getUserDateFormat(appContext);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [profileImageUrl, setProfileImageUrl] = useState();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { data } = props;
    const isEmpBadge = _get(data, 'isEmpBadge');

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     if (employerId) {
    //         loadProfileImageUrl({ id: employerId, setProfileImageUrl });
    //     }
    // }, [employerId]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, userDateFormat, userId, currentThreadId, draftReplies });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.messageLeft}>
                <div {...defns.messageLeftLink}>
                    <AfitdAvatar {...defns.profileImage} />
                </div>
                <div {...defns.flagIconContainer}>
                    <FlagIcon fontSize="small" {...defns.flagIcon} />
                </div>
            </div>
            <section {...defns.messageRight}>
                <div {...defns.messageRightLine1}>
                    <div {...defns.companyNameContainer}>
                        <AfitdTypography {...defns.companyNameText} />
                        {isEmpBadge && <VerifiedUserRounded {...defns.verifyIcon} />}
                    </div>
                    <div {...defns.lastMessageDt}>
                        <LastMessageAgeCounter {...defns.lastMessageDtText} />
                    </div>
                </div>
                <div {...defns.messageRightLine2}>
                    <div {...defns.jobTitle}>
                        <AfitdTypography {...defns.subjectText} />
                    </div>
                    <div {...defns.unreadMessageCount}>
                        <AfitdBadgeCount {...defns.unreadMessageCountText} />
                    </div>
                </div>
                <div {...defns.messageRightLine3}>
                    <div {...defns.lastMessage}>
                        <TextTruncate line={1} element="div" truncateText="…" text={defns.lastMessageText.value} />
                    </div>
                </div>
            </section>
            <div className="clear"></div>
        </div>
    );
};

export default ThreadSummaryBlock;
